import { useEffect, useRef, useMemo, useState } from 'react';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { GridRowModes, GridToolbarContainer, GridActionsCellItem, useGridApiRef } from '@mui/x-data-grid-premium';
import { Field, FieldArray, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import { multiply, round, divide, add, subtract } from 'mathjs';
import { Box, IconButton, Tooltip } from '@mui/material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import EditIcon from '@mui/icons-material/Edit';
import GridCheckIcon from '@mui/icons-material/Check';
import GridCloseIcon from '@mui/icons-material/Close';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';

import {
  dataGridPremiumColumns,
  generateAutocompleteItems,
  normalizeNumberInput,
  parseLocaleNumber,
} from '../../utils/helper';
import {
  FormErrorUI,
  InputUI,
  AutoCompleteSelectUI,
  AddButtonUi,
  DatagridPremiumUi,
  DatePickerUI,
  CheckboxUI,
} from '../Interface';
import useConfig from '../../context/ConfigContext';
import { formatCurrency, getRounding } from '../../utils/currencyHelper';
import priceListService from '../../service/priceList.service';
import { DocumentDirection } from '../../config/DocumentDirection';
import { DocStatus, DocStatusList } from '../../config/document';
import { InvManagementMethod, ItemGroupType } from '../../config/itemGroup';
import { NotificationType } from '../../config';
import addNotification from '../../utils/addNotification';
import useGenerateJokerCols from '../../hooks/useGenerateJokerCols';
import discountRuleService from '../../service/discountRule.service';
import { getLineEditableFields, isCellEditable } from '../../utils/DocHelper';
import dayjs from 'dayjs';
import { getProjectOrderNumberName } from '../../utils/getProjectOrderNumberName';
import useParam from '../../context/ParamContext';
import ItemDialog from '../Item/ItemDialog';
import ItemStockDialog from '../Item/ItemStockDialog';
import DocumentPrevPricesDialog from './DocumentPrevPricesDialog';
import masterDataService from '../../service/masterData.service';
import vatCodeService from '../../service/vatCode.service';
import { PriceFormField } from '../../config/priceList';
import { useDocumentLineGridSx } from './style/documentLineGridSx';
import { DocumentType } from '../../config/DocumentType';
import useWindowSubtractedHeight from '../../hooks/useWindowSubtractedHeight';
import { AreaType } from '../../config/companyGroup';

const EditToolbar = (props) => {
  const { disabled, push, handleClick } = props;

  return (
    <GridToolbarContainer>
      <AddButtonUi disabled={disabled} onClick={() => handleClick(push)} />
    </GridToolbarContainer>
  );
};

const DocumentLine = ({
  disabled,
  defaultField,
  autocompleteOptions,
  autocompleteData,
  documentData,
  selectedCompanyState,
  viewField,
  datagridIdentifiers,
  currentDocType,
  jokerLineFields,
  selectedValues,
  batchLineState,
  binLineState,
  setNoteDialogState,
  docTypes,
  setAutoCompleteData,
  setAutoCompleteOptions,
  docTotal,
  docTypeId,
}) => {
  const gridHeight = useWindowSubtractedHeight({ otherContentHeight: 610, minHeight: 480 });

  const apiRef = useGridApiRef();
  const focusedFieldRef = useRef(null);
  const skipRecalcNetPriceRef = useRef(new Set());
  const vatContextRef = useRef('');
  const { params } = useParam();
  const { values, validateField, setFieldValue, errors } = useFormikContext();
  const { config, getConfig } = useConfig();
  const { selectedCompany } = selectedCompanyState;
  const { setBatchLine } = batchLineState;
  const { setBinLine } = binLineState;

  const [itemDialogLineId, setItemDialogLineId] = useState(null);
  const [itemDialogOpen, setItemDialogOpen] = useState(false);

  const [itemStockDialogState, setItemStockDialogState] = useState({ open: false, data: null });
  const [prevPricesDialogState, setPrevPricesDialogState] = useState({ open: false, line: null });

  const handleItemStockDialogClose = () => {
    setItemStockDialogState({ open: false, data: null });
  };

  const handlePrevPricesDialogClose = () => {
    setPrevPricesDialogState({ open: false, line: null });
  };

  const [selectedDocCur, setSelectedDocCur] = useState(null);
  const [selectedOwnCur, setSelectedOwnCur] = useState(null);
  const [docRounding, setDocRounding] = useState(0);

  const documentLineGridSx = useDocumentLineGridSx();

  useEffect(() => {
    const docCur = autocompleteData.currency.find((c) => c.code === values.currencyCode);
    setSelectedDocCur(docCur);
    setDocRounding(getRounding(documentData.direction, docCur));
  }, [values.currencyCode, autocompleteData.currency, documentData.direction]);

  useEffect(() => {
    const ownCur = autocompleteData.currency.find((c) => c.code === config?.currencyCode);
    setSelectedOwnCur(ownCur);
  }, [autocompleteData.currency, config?.currencyCode, documentData.direction]);

  useEffect(() => {
    //docTotal(values, setFieldValue);
    recalculateAllLineFields();
  }, [values.docLine, values.exchangeRate]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      docTotal(values, setFieldValue);
    }, 120);

    return () => clearTimeout(timeoutId);
  }, [values.docLine, values.exchangeRate, values.roundingInDocCur, values.currencyCode, docTotal, setFieldValue]);

  useEffect(() => {
    if (!Object.keys(config).length) getConfig();
  }, [getConfig, config]);

  const focusFirstEditableCell = (rowId) => {
    const editableFields = getLineEditableFields(apiRef, rowId, { currentDocType, selectedValues });
    const fieldToFocus = editableFields[0] ?? 'itemCode';

    focusedFieldRef.current = fieldToFocus;
    apiRef.current.startRowEditMode({ id: rowId });
    apiRef.current.setCellFocus(rowId, fieldToFocus);
    focusEditorInput(rowId, fieldToFocus);
  };

  const createNewLineData = () => {
    const findedProject = autocompleteData?.project.find((p) => p.projectId === values.projectId);
    const findedItem = autocompleteData.item.find((u) => u.itemCode === defaultField.itemCode);
    const findedQtyUnit = autocompleteData.qtyUnit.find((u) => u.id === findedItem?.quantityUnitId);

    const resolvedVatCode = selectedCompany
      ? documentData.direction === DocumentDirection.INCOMING
        ? selectedCompany?.supplierVatCode
        : selectedCompany?.customerVatCode
      : null;

    const findedVat = autocompleteData.vatCode.find((u) => u.code === resolvedVatCode);

    return {
      ...defaultField,
      id: uuidv4(),
      isNew: true,
      dueDate: values.dueDate ?? defaultField.dueDate,
      vatCode: resolvedVatCode,
      vatPercent: findedVat?.percentage ?? 0,
      projectId: values.projectId,
      projectOrderNumber: findedProject?.orderNumber ?? '',
      costCenterCode: values.costCenterCode,
      whsCode: values.whsCode ?? defaultField.whsCode,
      itemName: findedItem ? findedItem.itemName : '',
      weightChangeNumber: findedItem ? findedItem.weightChangeNumber : null,
      quantityUnitId: findedItem ? findedItem.quantityUnitId : '',
      quantityUnitCode: findedQtyUnit ? findedQtyUnit.code : '',
      quantityUnitName: findedQtyUnit ? findedQtyUnit.name : '',
      landedCost: false,
    };
  };

  const handleClick = async (push) => {
    const newField = createNewLineData();

    await push(newField);
    focusFirstEditableCell(newField.id);

    const fieldsToValidate = [
      `docLine.${values.docLine.length ?? 0}.itemCode`,
      `docLine.${values.docLine.length ?? 0}.itemCodeInBp`,
      `docLine.${values.docLine.length ?? 0}.qty`,
      `docLine.${values.docLine.length ?? 0}.quantityUnitId`,
      `docLine.${values.docLine.length ?? 0}.whsCode`,
      `docLine.${values.docLine.length ?? 0}.toWhsCode`,
      `docLine.${values.docLine.length ?? 0}.unitPrice`,
      `docLine.${values.docLine.length ?? 0}.vatCode`,
      `docLine.${values.docLine.length ?? 0}.netPrice`,
      `docLine.${values.docLine.length ?? 0}.grossPrice`,
      `docLine.${values.docLine.length ?? 0}.dueDate`,
      `docLine.${values.docLine.length ?? 0}.weight`,
      `docLine.${values.docLine.length ?? 0}.projectId`,
      `docLine.${values.docLine.length ?? 0}.costCenterCode`,
      `docLine.${values.docLine.length ?? 0}.itemReference`,
      `docLine.${values.docLine.length ?? 0}.internalNote`,
      `docLine.${values.docLine.length ?? 0}.bpNote`,
    ];

    for (const field of fieldsToValidate) {
      validateField(field);
    }

    return defaultField.id;
  };

  const getSelectedAddressCountryCode = () => {
    const companyAddresses = selectedCompany?.companyAdress ?? [];

    const shippingAddress = companyAddresses.find((address) => address.id === values.shippingAdressId);
    if (shippingAddress?.countryCode) return shippingAddress.countryCode;

    const billingAddress = companyAddresses.find((address) => address.id === values.billingAdressId);
    if (billingAddress?.countryCode) return billingAddress.countryCode;

    return undefined;
  };

  const getAreaByCountryCode = (countryCode) => {
    if (!countryCode) return undefined;

    if (config?.countryCode && config.countryCode === countryCode) {
      return AreaType.BE;
    }

    const matchedCountry = autocompleteData.country?.find((country) => country.code === countryCode);
    if (!matchedCountry) return undefined;

    return matchedCountry.euMember === true ? AreaType.EU : AreaType.EK;
  };

  const getDefaultVatCodeForItem = (selectedItem) => {
    const { purchaseVatCodeService, purchaseVatCodeItem, salesVatCodeService, salesVatCodeItem } = config;

    return documentData.direction === DocumentDirection.INCOMING
      ? selectedItem.itemType === ItemGroupType.SERVICE
        ? purchaseVatCodeService
        : purchaseVatCodeItem
      : selectedItem.itemType === ItemGroupType.SERVICE
        ? salesVatCodeService
        : salesVatCodeItem;
  };

  const resolveVatForItem = async (itemCode, selectedItem) => {
    const defaultVatCode = getDefaultVatCodeForItem(selectedItem);
    let usedVat = autocompleteData.vatCode.find((vat) => vat.code === defaultVatCode);

    const countryCode = getSelectedAddressCountryCode();
    const area = getAreaByCountryCode(countryCode);

    const resolvedVatCode = await vatCodeService.getVatCodeToDoc({
      documentTypeId: documentData.documentTypeId,
      itemCode,
      companyId: values.companyId ?? null,
      area,
    });

    if (resolvedVatCode) {
      usedVat = autocompleteData.vatCode.find((vat) => vat.code === resolvedVatCode) ?? usedVat;
    }

    return usedVat ? { vatCode: usedVat.code, vatPercent: usedVat.percentage } : { vatCode: null, vatPercent: null };
  };

  const applyVatToLine = async (valueIndex, itemCode, selectedItemParam = null) => {
    const selectedItem = selectedItemParam ?? autocompleteData.item.find((item) => item.itemCode === itemCode);
    if (!selectedItem) return { vatCode: null, vatPercent: null };

    const vatObj = await resolveVatForItem(itemCode, selectedItem);

    setFieldValue(`docLine.${valueIndex}.vatCode`, vatObj.vatCode);
    setFieldValue(`docLine.${valueIndex}.vatPercent`, vatObj.vatPercent);

    apiRef.current.setEditCellValue({
      id: values.docLine[valueIndex].id,
      field: 'vatCode',
      value: vatObj.vatCode,
    });

    apiRef.current.setEditCellValue({
      id: values.docLine[valueIndex].id,
      field: 'vatPercent',
      value: vatObj.vatPercent,
    });

    calculatePrice(
      valueIndex,
      values.docLine[valueIndex].qty,
      values.docLine[valueIndex].unitPrice,
      vatObj.vatPercent ?? 0
    );

    const grossUnitPrice = round(
      calculateGrossPrice(values.docLine[valueIndex].unitPrice ?? 0, vatObj.vatPercent ?? 0),
      docRounding
    );

    setFieldValue(`docLine.${valueIndex}.grossUnitPrice`, grossUnitPrice);
    setFieldValue(`docLine.${valueIndex}.baseGrossUnitPrice`, grossUnitPrice);

    return vatObj;
  };

  const handleItemChange = async (valueIndex, value, fromPaste = false) => {
    setFieldValue(`docLine.${valueIndex}.itemCode`, value);

    const selectedItem = autocompleteData.item.find((i) => i.itemCode === value);
    if (selectedItem) {
      // item fő adatok
      setFieldValue(`docLine.${valueIndex}.itemName`, selectedItem?.itemName ?? null);
      setFieldValue(`docLine.${valueIndex}.quantityUnitId`, selectedItem?.quantityUnitId ?? null);
      const findedQtyUnit = autocompleteData.qtyUnit.find((u) => u.id === selectedItem?.quantityUnitId);
      setFieldValue(`docLine.${valueIndex}.quantityUnitCode`, findedQtyUnit?.code ?? null);
      setFieldValue(`docLine.${valueIndex}.quantityUnitName`, findedQtyUnit?.name ?? null);
      setFieldValue(`docLine.${valueIndex}.weightChangeNumber`, selectedItem?.weight ?? null);
      setFieldValue(`docLine.${valueIndex}.landedCost`, Boolean(selectedItem?.landedCost));

      calculateWeight(valueIndex, values.docLine[valueIndex].qty, selectedItem?.weight ?? null);
      const vatObj = await applyVatToLine(valueIndex, value, selectedItem);
      let pl = {};
      if (!fromPaste) {
        let netPrice = null;
        if (values.priceListId) {
          const findedPriceList = autocompleteData.priceList.find((pl) => pl.id === values.priceListId);

          pl = await priceListService.getItemPrice({
            itemCode: value,
            priceListId: values.priceListId,
          });
          if (pl?.price) {
            netPrice = pl.price;

            if (findedPriceList.priceForm === PriceFormField.GROSS) {
              netPrice = handleGrossPriceChange(valueIndex, pl.price, vatObj.vatPercent ?? 0);
              setFieldValue(`docLine.${valueIndex}.baseUnitPrice`, netPrice);
              setFieldValue(`docLine.${valueIndex}.grossUnitPrice`, pl.price);
            } else {
              const price = parseFloat(pl.price);
              setFieldValue(`docLine.${valueIndex}.unitPrice`, price);
              setFieldValue(`docLine.${valueIndex}.baseUnitPrice`, price);
              calculatePrice(valueIndex, values.docLine[valueIndex].qty, pl.price, vatObj.vatPercent ?? 0);
            }
          }
        }

        const discount = await discountRuleService.getDiscount({
          itemCode: value,
          companyId: values.companyId ?? null,
          priceListId: values.priceListId ?? null,
          date: values.docDate,
        });

        if (discount?.percent && discount?.id) {
          setFieldValue(`docLine.${valueIndex}.discountPercent`, discount.percent);
          setFieldValue(`docLine.${valueIndex}.ruleId`, discount.id);
          if (netPrice) {
            const price = parseFloat(pl.price);
            const unitPrice = calculateDiscountValue(discount.percent, price);
            setFieldValue(`docLine.${valueIndex}.unitPrice`, unitPrice);
            calculatePrice(valueIndex, values.docLine[valueIndex].qty, unitPrice, vatObj.vatPercent ?? 0);
          }
        }
      }
    } else {
      setFieldValue(`docLine.${valueIndex}.itemName`, null);
      setFieldValue(`docLine.${valueIndex}.quantityUnitId`, null);
      setFieldValue(`docLine.${valueIndex}.quantityUnitCode`, null);
      setFieldValue(`docLine.${valueIndex}.quantityUnitName`, null);
      setFieldValue(`docLine.${valueIndex}.weightChangeNumber`, null);
      setFieldValue(`docLine.${valueIndex}.unitPrice`, null);
      setFieldValue(`docLine.${valueIndex}.baseUnitPrice`, null);
      setFieldValue(`docLine.${valueIndex}.landedCost`, false);
    }
  };

  const refreshLineVatCodes = async () => {
    for (let index = 0; index < values.docLine.length; index += 1) {
      const line = values.docLine[index];

      if (!line?.itemCode || !line.isNew) continue;

      await applyVatToLine(index, line.itemCode);
    }
  };

  useEffect(() => {
    if (!values.docLine?.length || !autocompleteData.item?.length || !autocompleteData.vatCode?.length) {
      vatContextRef.current = '';
      return;
    }

    const vatContextKey = JSON.stringify({
      documentTypeId: documentData.documentTypeId ?? null,
      companyId: values.companyId ?? null,
      selectedCompanyId: selectedCompany?.id ?? null,
      shippingAdressId: values.shippingAdressId ?? null,
      billingAdressId: values.billingAdressId ?? null,
      ownCountryCode: config?.countryCode ?? null,
      countriesLoaded: autocompleteData.country?.length ?? 0,
    });

    if (vatContextRef.current === vatContextKey) return;
    vatContextRef.current = vatContextKey;

    refreshLineVatCodes();
  }, [
    values.companyId,
    values.shippingAdressId,
    values.billingAdressId,
    autocompleteData.country,
    autocompleteData.item,
    autocompleteData.vatCode,
    config?.countryCode,
  ]);

  const calculateGrossPrice = (netPrice, vatPercent) => {
    return multiply(netPrice ?? 0, add(1, divide(vatPercent, 100)));
  };

  const calculateNetPrice = (grossPrice, vatPercent) => {
    return divide(multiply(grossPrice ?? 0, 100), add(100, vatPercent));
  };

  const handlePriceChange = (valueIndex, value) => {
    const roundedValue = round(parseFloat(value ?? 0), docRounding);
    setFieldValue(`docLine.${valueIndex}.unitPrice`, roundedValue ?? '');

    setFieldValue(`docLine.${valueIndex}.discountPercent`, '');
    setFieldValue(`docLine.${valueIndex}.ruleId`, null);

    setFieldValue(`docLine.${valueIndex}.baseUnitPrice`, roundedValue);

    const vatPercent = values.docLine[valueIndex].vatPercent ?? 0;

    calculatePrice(valueIndex, values.docLine[valueIndex].qty, roundedValue ?? '0', vatPercent);

    const grossUnitPrice = round(calculateGrossPrice(roundedValue ?? 0, vatPercent), docRounding);

    setFieldValue(`docLine.${valueIndex}.grossUnitPrice`, grossUnitPrice);
    setFieldValue(`docLine.${valueIndex}.baseGrossUnitPrice`, grossUnitPrice);

    apiRef.current.setEditCellValue({
      id: values.docLine[valueIndex].id,
      field: 'discountPercent',
      value: null,
    });

    apiRef.current.setEditCellValue({
      id: values.docLine[valueIndex].id,
      field: 'baseGrossUnitPrice',
      value: grossUnitPrice,
    });
  };

  const handleGrossPriceChange = (valueIndex, value, vatPercent) => {
    const roundedValue = round(parseFloat(value ?? 0), docRounding);
    setFieldValue(`docLine.${valueIndex}.grossUnitPrice`, roundedValue ?? '');
    setFieldValue(`docLine.${valueIndex}.baseGrossUnitPrice`, roundedValue ?? '');

    const netUnitPrice = round(calculateNetPrice(roundedValue ?? 0, vatPercent), docRounding);
    setFieldValue(`docLine.${valueIndex}.unitPrice`, netUnitPrice);
    setFieldValue(`docLine.${valueIndex}.baseUnitPrice`, netUnitPrice);

    setFieldValue(`docLine.${valueIndex}.discountPercent`, null);
    setFieldValue(`docLine.${valueIndex}.ruleId`, null);

    apiRef.current.setEditCellValue({
      id: values.docLine[valueIndex].id,
      field: 'baseGrossUnitPrice',
      value: roundedValue,
    });

    apiRef.current.setEditCellValue({
      id: values.docLine[valueIndex].id,
      field: 'discountPercent',
      value: null,
    });

    apiRef.current.setEditCellValue({
      id: values.docLine[valueIndex].id,
      field: 'baseUnitPrice',
      value: netUnitPrice,
    });

    calculatePrice(valueIndex, values.docLine[valueIndex].qty, netUnitPrice, vatPercent);

    return netUnitPrice;
  };

  const handleNetPriceChange = (valueIndex, value) => {
    const roundedNetPrice = round(parseFloat(value ?? 0), selectedDocCur?.amountRounding ?? 0);
    const qty = parseFloat(values.docLine[valueIndex].qty ?? '0');
    const vatPercent = values.docLine[valueIndex].vatPercent ?? 0;

    if (values.docLine[valueIndex]?.id) {
      skipRecalcNetPriceRef.current.add(values.docLine[valueIndex].id);
    }

    setFieldValue(`docLine.${valueIndex}.netPrice`, roundedNetPrice ?? '');

    const netUnitPrice = qty > 0 ? round(divide(roundedNetPrice ?? 0, qty), docRounding) : 0;
    setFieldValue(`docLine.${valueIndex}.unitPrice`, netUnitPrice);
    setFieldValue(`docLine.${valueIndex}.baseUnitPrice`, netUnitPrice);

    setFieldValue(`docLine.${valueIndex}.discountPercent`, null);
    setFieldValue(`docLine.${valueIndex}.ruleId`, null);

    const grossUnitPrice = round(calculateGrossPrice(netUnitPrice ?? 0, vatPercent), docRounding);
    setFieldValue(`docLine.${valueIndex}.grossUnitPrice`, grossUnitPrice);
    setFieldValue(`docLine.${valueIndex}.baseGrossUnitPrice`, grossUnitPrice);

    const vatValue =
      vatPercent > 0
        ? round(multiply(roundedNetPrice, divide(vatPercent, 100)), selectedDocCur?.amountRounding ?? 0)
        : 0;
    const grossPrice = round(add(roundedNetPrice, vatValue), docRounding);

    setFieldValue(`docLine.${valueIndex}.vatValue`, vatValue);
    setFieldValue(`docLine.${valueIndex}.grossPrice`, grossPrice);

    const exchangeRate = Number(values.exchangeRate);
    const netPriceInOwnCur = round(
      multiply(roundedNetPrice, exchangeRate > 0 ? exchangeRate : 1),
      selectedOwnCur?.amountRounding ?? 0
    );
    const vatValueInOwnCur = round(
      multiply(vatValue, exchangeRate > 0 ? exchangeRate : 1),
      selectedOwnCur?.amountRounding ?? 0
    );
    const grossPriceInOwnCur = round(
      multiply(grossPrice, exchangeRate > 0 ? exchangeRate : 1),
      selectedOwnCur?.amountRounding ?? 0
    );

    setFieldValue(`docLine.${valueIndex}.netPriceInOwnCur`, netPriceInOwnCur);
    setFieldValue(`docLine.${valueIndex}.vatValueInOwnCur`, vatValueInOwnCur);
    setFieldValue(`docLine.${valueIndex}.grossPriceInOwnCur`, grossPriceInOwnCur);

    apiRef.current.setEditCellValue({
      id: values.docLine[valueIndex].id,
      field: 'netPrice',
      value: roundedNetPrice,
    });
  };

  const handleQtyChange = (valueIndex, value) => {
    const normalizedValue = normalizeNumberInput(value);
    const inputValue = parseLocaleNumber(value);
    const cplQty = parseFloat(values.docLine?.[valueIndex]?.cplQty ?? 0);
    const isNewLine = Boolean(values.docLine?.[valueIndex]?.isNew);

    if (inputValue < cplQty) {
      addNotification({
        content: 'A mennyiség nem lehet kisebb a lezárt mennyiségnél!',
        type: NotificationType.ERROR,
      });

      return;
    }

    setFieldValue(`docLine.${valueIndex}.qty`, normalizedValue ?? '');

    if (isNaN(inputValue)) return;

    const rawOpnQty = subtract(inputValue, cplQty);
    const newOpnQty = rawOpnQty <= 0 ? 0 : round(rawOpnQty, 6);

    setFieldValue(`docLine.${valueIndex}.opnQty`, newOpnQty);

    // New (unsaved) lines should stay editable; don't auto-close them during typing.
    if (isNewLine) {
      setFieldValue(`docLine.${valueIndex}.lineStatus`, DocStatus.OPENED);
    } else {
      setFieldValue(`docLine.${valueIndex}.lineStatus`, newOpnQty === 0 ? DocStatus.CLOSED : DocStatus.OPENED);
    }

    calculatePrice(
      valueIndex,
      normalizedValue ?? '0',
      values.docLine[valueIndex].unitPrice,
      values.docLine[valueIndex].vatPercent
    );

    calculateWeight(valueIndex, normalizedValue ?? '0', values.docLine[valueIndex].weightChangeNumber);
  };

  const getValueIndex = (values, id) => {
    return values?.docLine?.findIndex((rc) => rc.id === id);
  };

  const isPinnedRow = (params) => params?.rowNode?.type === 'pinnedRow' || params?.id === 'pinned';

  const jokerColumns = useGenerateJokerCols(jokerLineFields, 'docLine', getValueIndex);

  const recalculateAllLineFields = () => {
    if (values?.docId) return;
    values?.docLine?.forEach((line, index) => {
      const qty = line.qty ?? 0;
      const unitPrice = line.unitPrice ?? 0;
      const vatPercent = line.vatPercent ?? 0;
      const weightChangeNumber = line.weightChangeNumber ?? 0;

      if (skipRecalcNetPriceRef.current.has(line.id)) {
        skipRecalcNetPriceRef.current.delete(line.id);
      } else {
        calculatePrice(index, qty, unitPrice, vatPercent);
      }
      calculateWeight(index, qty, weightChangeNumber);
    });
  };

  const calculateDiscountValue = (discountPercent, baseUnitPrice) => {
    const base = parseFloat(baseUnitPrice ?? 0);
    const percent = parseFloat(discountPercent ?? 0);

    if (!base) return 0;
    if (!percent) return base;

    const discountValue = multiply(base, divide(percent, 100));
    return round(subtract(base, discountValue), docRounding);
  };

  const handleDiscountPercentChange = (valueIndex, value) => {
    if (!value) {
      setFieldValue(`docLine.${valueIndex}.discountPercent`, null);
      setFieldValue(`docLine.${valueIndex}.ruleId`, null);
      setFieldValue(
        `docLine.${valueIndex}.unitPrice`,
        values.docLine[valueIndex].baseUnitPrice ?? values.docLine[valueIndex].unitPrice
      );

      calculatePrice(
        valueIndex,
        values.docLine[valueIndex].qty,
        values.docLine[valueIndex].baseUnitPrice,
        values.docLine[valueIndex].vatPercent
      );
    }

    const roundedValue = round(parseFloat(value ?? 0), 2);
    setFieldValue(`docLine.${valueIndex}.discountPercent`, roundedValue ?? '');
    setFieldValue(`docLine.${valueIndex}.ruleId`, null);

    const unitPrice = calculateDiscountValue(
      roundedValue,
      values.docLine[valueIndex].baseUnitPrice || values.docLine[valueIndex].unitPrice || 0
    );
    if (!values.docLine[valueIndex].baseUnitPrice) {
      setFieldValue(`docLine.${valueIndex}.baseUnitPrice`, values.docLine[valueIndex].unitPrice);
    }

    setFieldValue(`docLine.${valueIndex}.unitPrice`, unitPrice);
    calculatePrice(valueIndex, values.docLine[valueIndex].qty, unitPrice, values.docLine[valueIndex].vatPercent);
  };

  const handleItemDialogOpen = (rowId) => {
    setItemDialogLineId(rowId);
    setItemDialogOpen(true);
  };

  const selectItemFromDialog = async (itemCode) => {
    setItemDialogOpen(false);

    if (!itemCode || itemDialogLineId == null) {
      setItemDialogLineId(null);
      return;
    }

    const index = getValueIndex(values, itemDialogLineId);
    setItemDialogLineId(null);

    if (index === -1 || !values.docLine?.[index]) return;

    // autocompletedata, autocompleteoptions frissítése, ha nincs benne a kiválasztott cikk

    const findedItem = autocompleteData.item.find((i) => i.itemCode === itemCode);

    if (!findedItem) {
      const itm = await masterDataService.itemsFromTableToFormByItemCode(itemCode);

      if (!itm) {
        addNotification({
          content: 'Hiba történt a cikk adatainak lekérésekor!',
          type: NotificationType.ERROR,
        });
      }

      const itemData = itm[0];

      const newAutocompleteData = { ...autocompleteData };
      newAutocompleteData.item.push(itemData);
      setAutoCompleteData(newAutocompleteData);

      const newAutocompleteOptions = { ...autocompleteOptions };
      newAutocompleteOptions.item.push({ label: itemData.itemCode, value: itemData.itemCode });
      setAutoCompleteOptions(newAutocompleteOptions);
    }

    handleItemChange(index, itemCode);

    const currentQty = values.docLine?.[index]?.qty;
    if (!currentQty || Number(currentQty) === 0) {
      handleQtyChange(index, 1);
    }
  };

  const pinnedLine = useMemo(() => {
    const totals = (values?.docLine ?? []).reduce(
      (acc, line) => {
        const net = parseFloat(line?.netPrice ?? 0);
        const vat = parseFloat(line?.vatValue ?? 0);
        const gross = parseFloat(line?.grossPrice ?? 0);
        const weight = parseFloat(line?.weight ?? 0);
        const netOwn = parseFloat(line?.netPriceInOwnCur ?? 0);
        const vatOwn = parseFloat(line?.vatValueInOwnCur ?? 0);
        const grossOwn = parseFloat(line?.grossPriceInOwnCur ?? 0);

        return {
          netPrice: add(acc.netPrice, isNaN(net) ? 0 : net),
          vatValue: add(acc.vatValue, isNaN(vat) ? 0 : vat),
          grossPrice: add(acc.grossPrice, isNaN(gross) ? 0 : gross),
          weight: add(acc.weight, isNaN(weight) ? 0 : weight),
          netPriceInOwnCur: add(acc.netPriceInOwnCur, isNaN(netOwn) ? 0 : netOwn),
          vatValueInOwnCur: add(acc.vatValueInOwnCur, isNaN(vatOwn) ? 0 : vatOwn),
          grossPriceInOwnCur: add(acc.grossPriceInOwnCur, isNaN(grossOwn) ? 0 : grossOwn),
        };
      },
      {
        netPrice: 0,
        vatValue: 0,
        grossPrice: 0,
        weight: 0,
        netPriceInOwnCur: 0,
        vatValueInOwnCur: 0,
        grossPriceInOwnCur: 0,
      }
    );

    return {
      id: 'pinned',
      netPrice: round(totals.netPrice, docRounding),
      vatValue: round(totals.vatValue, docRounding),
      grossPrice: round(totals.grossPrice, docRounding),
      weight: round(totals.weight, 2),
      netPriceInOwnCur: round(totals.netPriceInOwnCur, selectedOwnCur?.amountRounding ?? 0),
      vatValueInOwnCur: round(totals.vatValueInOwnCur, selectedOwnCur?.amountRounding ?? 0),
      grossPriceInOwnCur: round(totals.grossPriceInOwnCur, selectedOwnCur?.amountRounding ?? 0),
    };
  }, [values?.docLine, docRounding]);

  const baseIsCellEditable = useMemo(
    () => isCellEditable({ currentDocType, selectedValues }),
    [currentDocType, selectedValues]
  );

  const columns = useMemo(
    () => (remove) =>
      dataGridPremiumColumns([
        {
          field: 'itemCode',
          headerName: 'Cikkszám',
          type: 'string',
          editable: true,
          sortable: false,
          renderCell: (data) => {
            if (isPinnedRow(data)) return '';
            const valueIndex = getValueIndex(values, data.id);
            const findedItem = autocompleteOptions?.item.find(
              (u) => u.value === values.docLine?.[valueIndex]?.itemCode
            );

            return findedItem?.value;
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const findedItem = autocompleteOptions?.item.find(
              (u) => u.value === values.docLine?.[valueIndex]?.itemCode
            );
            return (
              <>
                <Field
                  name={`docLine.${valueIndex}.itemCode`}
                  component={AutoCompleteSelectUI}
                  helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.itemCode} />}
                  //value={findedItem}
                  selectedValue={findedItem?.value}
                  selectedLabelValue={findedItem?.label}
                  onChange={async (_e, newVal) => {
                    const val = newVal?.value ?? null;
                    handleItemChange(valueIndex, val);

                    data.api.setEditCellValue({
                      id: data.id,
                      field: data.field,
                      value: val,
                    });
                  }}
                  dataset={autocompleteOptions?.item}
                  onBlur={async () => await validateField(`docLine.${valueIndex}.itemCode`)}
                />
                <Box>
                  <AddButtonUi onClick={() => handleItemDialogOpen(data.id)} />
                </Box>
              </>
            );
          },
        },
        {
          field: 'itemName',
          headerName: 'Cikknév',
          type: 'string',
          editable: true,
          sortable: false,
          renderCell: (data) => {
            if (isPinnedRow(data)) return '';
            const valueIndex = getValueIndex(values, data.id);
            return values.docLine?.[valueIndex]?.itemName ?? '';
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            return (
              <Field
                name={`docLine.${valueIndex}.itemName`}
                as={InputUI}
                variant="standard"
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.itemName} />}
                onChange={(e) => {
                  setFieldValue(`docLine.${valueIndex}.itemName`, e.target.value ?? null);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: e.target.value,
                  });
                }}
                onBlur={async () => await validateField(`docLine.${valueIndex}.itemName`)}
              />
            );
          },
        },
        {
          field: 'itemCodeInBp',
          headerName: 'ÜP cikkszám',
          type: 'string',
          editable: true,
          sortable: false,

          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);

            return (
              <Field
                name={`docLine.${valueIndex}.itemCodeInBp`}
                as={InputUI}
                type="text"
                variant="standard"
                fullWidth
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.itemCodeInBp} />}
                onChange={(e) => {
                  setFieldValue(`docLine.${valueIndex}.itemCodeInBp`, e.target.value ?? null);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: e.target.value,
                  });
                }}
                onBlur={async () => await validateField(`docLine.${valueIndex}.itemCodeInBp`)}
              />
            );
          },
        },
        {
          field: 'qty',
          headerName: 'Mennyiség',
          type: 'number',
          editable: true,
          sortable: false,
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const item = autocompleteData.item.find((i) => i.itemCode === values.docLine?.[valueIndex]?.itemCode);
            const whs = autocompleteData.warehouse.find(
              (w) => w.code === (values.docLine?.[valueIndex]?.whsCode || values.whsCode)
            );

            return (
              <>
                <Field
                  name={`docLine.${valueIndex}.qty`}
                  type="number"
                  variant="standard"
                  as={InputUI}
                  fullWidth
                  helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.qty} />}
                  onChange={(e) => {
                    const rawValue = e.target.value;
                    const normalizedValue = normalizeNumberInput(rawValue);
                    handleQtyChange(valueIndex, rawValue);

                    data.api.setEditCellValue({
                      id: data.id,
                      field: data.field,
                      value: normalizedValue,
                    });
                  }}
                  onBlur={async () => await validateField(`docLine.${valueIndex}.qty`)}
                />
                {((item?.invManagementMethod === InvManagementMethod.BATCH && currentDocType.inventoryAccounting) ||
                  (whs?.binManaged && currentDocType.inventoryAccounting && item?.type !== ItemGroupType.SERVICE)) && (
                  <IconButton
                    aria-label="edit"
                    onClick={() => {
                      setBatchLine({ ...data.row, selectedLineIndex: valueIndex });
                      setBinLine({ ...data.row, selectedLineIndex: valueIndex });
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                )}
              </>
            );
          },
        },
        {
          field: 'quantityUnitCode',
          headerName: 'ME',
          type: 'string',
          editable: false,
          sortable: false,
        },
        {
          field: 'opnQty',
          headerName: 'Nyitott',
          type: 'number',
          editable: false,
          sortable: false,
        },
        {
          field: 'cplQty',
          headerName: 'Lezárt',
          type: 'number',
          editable: false,
          sortable: false,
        },
        {
          field: 'whsCode',
          headerName: 'Raktár',
          type: 'string',
          editable: true,
          sortable: false,
          aggregable: false,
          renderCell: (data) => {
            if (isPinnedRow(data)) return '';
            const valueIndex = getValueIndex(values, data.id);
            const findedItem = autocompleteOptions?.warehouse.find((u) => {
              const docLineCode = values.docLine?.[valueIndex]?.whsCode;
              return u.value === (docLineCode || values.whsCode);
            });

            return findedItem?.label;
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const findedItem = autocompleteOptions?.warehouse.find((u) => {
              const docLineCode = values.docLine?.[valueIndex]?.whsCode;
              return u.value === (docLineCode || values.whsCode);
            });

            const line = values.docLine?.[valueIndex] ?? data.row;
            const itemCode = line?.itemCode ?? null;
            const isStockBtnDisabled = !itemCode;
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Field
                    name={`docLine.${valueIndex}.whsCode`}
                    as={AutoCompleteSelectUI}
                    helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.whsCode} />}
                    selectedValue={values.docLine?.[valueIndex]?.whsCode}
                    selectedLabelValue={findedItem?.value}
                    //value={findedItem}
                    onChange={(_e, newVal) => {
                      setFieldValue(`docLine.${valueIndex}.whsCode`, newVal?.value ?? null);
                      data.api.setEditCellValue({
                        id: data.id,
                        field: data.field,
                        value: newVal?.value ?? null,
                      });

                      // hozzárendelt sarzsok és rakhelyek ürítése
                      setFieldValue(`docLine.${valueIndex}.docLineBatchSn`, []);
                      setFieldValue(`docLine.${valueIndex}.docLineBin`, []);
                    }}
                    dataset={autocompleteOptions?.warehouse}
                    onBlur={async () => await validateField(`docLine.${valueIndex}.whsCode`)}
                  />
                </Box>

                <Tooltip title={isStockBtnDisabled ? 'Előbb adj meg cikkszámot' : 'Készlet információ'}>
                  <span>
                    <IconButton
                      aria-label="Készlet információ"
                      size="small"
                      disabled={isStockBtnDisabled}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        if (!itemCode) return;

                        const itemFromAutocomplete = autocompleteData.item.find((i) => i.itemCode === itemCode);

                        setItemStockDialogState({
                          open: true,
                          data: itemFromAutocomplete,
                        });
                      }}
                    >
                      <Inventory2OutlinedIcon fontSize="inherit" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            );
          },
        },
        {
          field: 'toWhsCode',
          headerName: 'Fogadó raktár',
          type: 'string',
          editable: true,
          sortable: false,
          aggregable: false,
          renderCell: (data) => {
            if (isPinnedRow(data)) return '';
            const valueIndex = getValueIndex(values, data.id);
            const findedItem = autocompleteOptions?.warehouse.find((u) => {
              const docLineCode = values.docLine?.[valueIndex]?.toWhsCode;
              return u.value === (docLineCode || values.toWhsCode);
            });

            return findedItem?.label;
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const findedItem = autocompleteOptions?.warehouse.find((u) => {
              const docLineCode = values.docLine?.[valueIndex]?.toWhsCode;
              return u.value === (docLineCode || values.toWhsCode);
            });
            return (
              <Field
                name={`docLine.${valueIndex}.toWhsCode`}
                as={AutoCompleteSelectUI}
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.toWhsCode} />}
                //selectedValue={values.docLine?.[valueIndex]?.toWhsCode}
                value={findedItem}
                onChange={(_e, newVal) => {
                  setFieldValue(`docLine.${valueIndex}.toWhsCode`, newVal?.value ?? null);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: newVal?.value ?? null,
                  });
                  // default bin setelése raktárból
                  const findedWarehouse = autocompleteData.warehouse.find((whs) => whs.code === newVal?.value);
                  if (findedWarehouse?.binManaged) {
                    setFieldValue(`docLine.${valueIndex}.toBinId`, findedWarehouse.dispatchBin ?? null);
                    data.api.setEditCellValue({
                      id: data.id,
                      field: 'toBinId',
                      value: findedWarehouse.dispatchBin ?? null,
                    });

                    return;
                  }

                  setFieldValue(`docLine.${valueIndex}.toBinId`, null);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: 'toBinId',
                    value: null,
                  });
                }}
                dataset={autocompleteOptions?.warehouse}
                onBlur={async () => await validateField(`docLine.${valueIndex}.toWhsCode`)}
              />
            );
          },
        },
        {
          field: 'toBinId',
          headerName: 'Fogadó rakhely',
          type: 'string',
          editable: true,
          sortable: false,
          aggregable: false,
          renderCell: (data) => {
            if (isPinnedRow(data)) return '';
            const valueIndex = getValueIndex(values, data.id);
            const findedWarehouse = autocompleteData.warehouse.find(
              (whs) => whs.code === values.docLine?.[valueIndex]?.toWhsCode
            );

            const findedBin = findedWarehouse?.bin.find((bin) => bin.binId === values.docLine?.[valueIndex]?.toBinId);

            return findedBin?.binCode ?? '';
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const findedWarehouse = autocompleteData.warehouse.find(
              (whs) => whs.code === values.docLine?.[valueIndex]?.toWhsCode
            );
            const binOptions = generateAutocompleteItems(findedWarehouse?.bin, 'binCode', 'binId');

            const findedItem = binOptions.find((b) => b.value === values.docLine?.[valueIndex]?.toBinId);
            const toWhsKey = values.docLine?.[valueIndex]?.toWhsCode ?? 'no-whs';

            return (
              <Field
                key={`to-bin-${valueIndex}-${toWhsKey}`}
                name={`docLine.${valueIndex}.toBinId`}
                as={AutoCompleteSelectUI}
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.toBinId} />}
                //selectedValue={values.docLine?.[valueIndex]?.toBinId}
                value={findedItem}
                onChange={(_e, newVal) => {
                  setFieldValue(`docLine.${valueIndex}.toBinId`, newVal?.value ?? null);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: newVal?.value ?? null,
                  });
                }}
                dataset={binOptions}
                onBlur={async () => await validateField(`docLine.${valueIndex}.toBinId`)}
              />
            );
          },
        },
        {
          field: 'unitPrice',
          headerName: 'Egységár',
          type: 'number',
          editable: true,
          sortable: false,
          renderCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            return values.docLine?.[valueIndex]?.unitPrice
              ? formatCurrency(values.docLine?.[valueIndex]?.unitPrice, docRounding)
              : ' ';
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);

            return (
              <Field
                name={`docLine.${valueIndex}.unitPrice`}
                type="number"
                variant="standard"
                as={InputUI}
                fullWidth
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.unitPrice} />}
                onChange={(e) => {
                  const value = e.target.value;
                  handlePriceChange(valueIndex, e.target.value ?? '');

                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: value,
                  });
                }}
                onBlur={async () => await validateField(`docLine.${valueIndex}.unitPrice`)}
              />
            );
          },
        },

        {
          field: 'grossUnitPrice',
          headerName: 'Bruttó egységár',
          type: 'number',
          editable: false,
          sortable: false,
          renderCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            return values.docLine?.[valueIndex]?.grossUnitPrice
              ? formatCurrency(values.docLine?.[valueIndex]?.grossUnitPrice ?? '', docRounding)
              : '';
          },
        },

        {
          field: 'discountPercent',
          headerName: 'Kedvezmény %',
          type: 'number',
          editable: true,
          sortable: false,
          valueGetter: (value) => parseFloat(value),
          valueFormatter: (params) => {
            return params ? `${formatCurrency(params)} %` : '';
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);

            return (
              <Field
                name={`docLine.${valueIndex}.discountPercent`}
                type="number"
                variant="standard"
                as={InputUI}
                value={values.docLine?.[valueIndex]?.discountPercent ?? ''}
                fullWidth
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.discountPercent} />}
                onChange={(e) => {
                  const value = e.target.value;
                  handleDiscountPercentChange(valueIndex, e.target.value ?? '');

                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: value,
                  });
                }}
                onBlur={async () => await validateField(`docLine.${valueIndex}.discountPercent`)}
              />
            );
          },
        },

        {
          field: 'baseUnitPrice',
          headerName: 'Alap egységár',
          type: 'number',
          editable: false,
          sortable: false,
          valueGetter: (value) => parseFloat(value),
          valueFormatter: (params) => {
            return params ? formatCurrency(params, docRounding) : '';
          },
        },

        {
          field: 'baseGrossUnitPrice',
          headerName: 'Alap bruttó egységár',
          type: 'number',
          editable: true,
          sortable: false,
          valueGetter: (value) => {
            return parseFloat(value);
          },
          valueFormatter: (params) => {
            return params ? formatCurrency(params, docRounding) : '';
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);

            return (
              <Field
                name={`docLine.${valueIndex}.baseGrossUnitPrice`}
                type="number"
                variant="standard"
                as={InputUI}
                fullWidth
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.baseGrossUnitPrice} />}
                onChange={(e) => {
                  const value = e.target.value;
                  handleGrossPriceChange(valueIndex, e.target.value ?? '', values.docLine?.[valueIndex]?.vatPercent);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: value,
                  });
                }}
                onBlur={async () => await validateField(`docLine.${valueIndex}.baseGrossUnitPrice`)}
              />
            );
          },
        },

        {
          field: 'currencyCode',
          headerName: 'Ár pénznem',
          type: 'string',
          editable: false,
          sortable: false,
          renderCell: (data) => {
            if (isPinnedRow(data)) return '';
            const currFromPl = autocompleteOptions?.priceList.find((u) => u.id === values.priceListId);
            return currFromPl?.currencyCode ?? values.currencyCode;
          },
        },

        {
          field: 'vatCode',
          headerName: 'Áfakód',
          type: 'string',
          editable: true,
          sortable: false,
          renderCell: (data) => {
            if (isPinnedRow(data)) return '';
            const valueIndex = getValueIndex(values, data.id);
            const findedItem = autocompleteOptions?.vatCode.find((u) => {
              return u.value === values.docLine?.[valueIndex]?.vatCode;
            });

            return findedItem?.label;
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const currentVatCode = data.value ?? data.row?.vatCode ?? values.docLine?.[valueIndex]?.vatCode ?? null;
            const findedItem = autocompleteOptions?.vatCode.find((u) => {
              return u.value === currentVatCode;
            });
            return (
              <Field
                name={`docLine.${valueIndex}.vatCode`}
                as={AutoCompleteSelectUI}
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.vatCode} />}
                selectedValue={currentVatCode}
                selectedLabelValue={findedItem?.label}
                value={findedItem}
                onChange={(_e, newVal) => {
                  setFieldValue(`docLine.${valueIndex}.vatCode`, newVal?.value ?? null);
                  const findedVat = autocompleteData.vatCode.find((u) => u.code === newVal?.value);
                  setFieldValue(`docLine.${valueIndex}.vatPercent`, findedVat?.percentage ?? null);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: newVal?.value ?? null,
                  });
                  calculatePrice(
                    valueIndex,
                    values.docLine[valueIndex].qty,
                    values.docLine[valueIndex].unitPrice,
                    findedVat?.percentage ?? '0'
                  );

                  const grossPrice = round(
                    calculateGrossPrice(values.docLine[valueIndex].unitPrice, findedVat?.percentage ?? '0'),
                    docRounding
                  );
                  setFieldValue(`docLine.${valueIndex}.grossUnitPrice`, grossPrice);
                  setFieldValue(`docLine.${valueIndex}.baseGrossUnitPrice`, grossPrice);

                  apiRef.current.setEditCellValue({
                    id: data.id,
                    field: 'baseGrossUnitPrice',
                    value: grossPrice,
                  });
                }}
                dataset={autocompleteOptions?.vatCode}
                onBlur={async () => await validateField(`docLine.${valueIndex}.vatCode`)}
              />
            );
          },
        },

        {
          field: 'vatPercent',
          headerName: 'Áfa %',
          type: 'number',
          editable: false,
          sortable: false,
        },

        {
          field: 'netPrice',
          headerName: 'Nettó érték',
          type: 'number',
          editable: true,
          sortable: false,
          valueGetter: (value) => parseFloat(value),
          valueFormatter: (params) => {
            return params ? formatCurrency(params, selectedDocCur?.amountRounding ?? 0) : '';
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);

            return (
              <Field
                name={`docLine.${valueIndex}.netPrice`}
                type="number"
                variant="standard"
                as={InputUI}
                fullWidth
                //disabled={!editable}
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.netPrice} />}
                onChange={(e) => {
                  const value = e.target.value;

                  // ha nincs áfakód noti
                  // ha nincs mennyiség noti

                  if (values.docLine?.[valueIndex]?.vatCode == null) {
                    addNotification({
                      content: 'Kérem válasszon áfakódot a sorhoz, mielőtt megadja a nettó értéket!',
                      type: NotificationType.WARNING,
                    });
                    return;
                  }

                  if (!values.docLine?.[valueIndex]?.qty || Number(values.docLine?.[valueIndex]?.qty) === 0) {
                    addNotification({
                      content: 'Kérem adjon meg mennyiséget a sorhoz, mielőtt megadja a nettó értéket!',
                      type: NotificationType.WARNING,
                    });
                    return;
                  }

                  handleNetPriceChange(valueIndex, value ?? '');
                }}
                onBlur={async () => await validateField(`docLine.${valueIndex}.netPrice`)}
              />
            );
          },
        },

        {
          field: 'vatValue',
          headerName: 'Áfa érték',
          type: 'number',
          editable: false,
          sortable: false,
          valueGetter: (value) => parseFloat(value),
          valueFormatter: (params) => {
            return params ? formatCurrency(params, selectedDocCur?.amountRounding ?? 0) : '';
          },
        },

        {
          field: 'grossPrice',
          headerName: 'Bruttó érték',
          type: 'number',
          editable: false,
          sortable: false,
          valueGetter: (value) => parseFloat(value),
          valueFormatter: (params) => {
            return params ? formatCurrency(params, selectedDocCur?.amountRounding ?? 0) : '';
          },
        },

        {
          field: 'netPriceInOwnCur',
          headerName: 'Nettó saját pénznemben',
          type: 'number',
          editable: false,
          sortable: false,
          valueGetter: (value) => parseFloat(value),
          valueFormatter: (params) => {
            return params ? formatCurrency(params, selectedOwnCur?.amountRounding ?? 0) : '';
          },
        },

        {
          field: 'vatValueInOwnCur',
          headerName: 'Áfa saját pénznemben',
          type: 'number',
          editable: false,
          sortable: false,
          valueGetter: (value) => parseFloat(value),
          valueFormatter: (params) => {
            return params ? formatCurrency(params, selectedOwnCur?.amountRounding ?? 0) : '';
          },
        },

        {
          field: 'grossPriceInOwnCur',
          headerName: 'Bruttó saját pénznemben',
          type: 'number',
          editable: false,
          sortable: false,
          valueGetter: (value) => parseFloat(value),
          valueFormatter: (params) => {
            return params ? formatCurrency(params, selectedOwnCur?.amountRounding ?? 0) : '';
          },
        },
        ...(documentData.additionalCost
          ? [
              {
                field: 'finalUnitCost',
                headerName: 'Költséggel növelt egységár saját pénznemben',
                type: 'number',
                editable: false,
                sortable: false,
                valueGetter: (value) => (value ? parseFloat(value) : ''),
                valueFormatter: (params) => {
                  return params ? formatCurrency(params, docRounding) : '';
                },
              },
              {
                field: 'finalLineValue',
                headerName: 'Költséggel növelt nettó saját pénznemben',
                type: 'number',
                editable: false,
                sortable: false,
                valueGetter: (value) => (value ? parseFloat(value) : ''),
                valueFormatter: (params) => {
                  return params ? formatCurrency(params, docRounding) : '';
                },
              },
              {
                field: 'landedCost',
                headerName: 'Mellékköltség',
                type: 'boolean',
                editable: true,
                sortable: false,
                valueGetter: (value, row) => {
                  const valueIndex = getValueIndex(values, row.id);
                  return values.docLine?.[valueIndex]?.landedCost;
                },
                renderEditCell: (data) => {
                  const valueIndex = getValueIndex(values, data.id);
                  const findedItem = autocompleteData.item.find(
                    (i) => i.itemCode === values.docLine?.[valueIndex]?.itemCode
                  );
                  return (
                    <Field
                      name={`docLine.${valueIndex}.landedCost`}
                      type="checkbox"
                      component={CheckboxUI}
                      helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.landedCost} />}
                      checked={values.docLine?.[valueIndex]?.landedCost ?? false}
                      variant="standard"
                      disabled={findedItem?.type !== 'SERVICE'}
                      onChange={(e) => {
                        setFieldValue(`docLine.${valueIndex}.landedCost`, e.target.checked);
                        data.api.setEditCellValue({
                          id: data.id,
                          field: data.field,
                          value: e.target.checked,
                        });
                      }}
                    />
                  );
                },
              },
            ]
          : []),

        {
          field: 'weightChangeNumber',
          headerName: 'Súly váltó',
          type: 'number',
          editable: false,
          sortable: false,
        },

        {
          field: 'weight',
          headerName: 'Súly',
          type: 'number',
          editable: true,
          sortable: false,
          valueGetter: (value) => parseFloat(value),
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);

            return (
              <Field
                name={`docLine.${valueIndex}.weight`}
                type="number"
                variant="standard"
                as={InputUI}
                fullWidth
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.weight} />}
                onChange={(e) => {
                  setFieldValue(`docLine.${valueIndex}.weight`, e.target.value ?? '');
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: e.target.value,
                  });
                }}
                onBlur={async () => await validateField(`docLine.${valueIndex}.weight`)}
              />
            );
          },
        },

        {
          field: 'projectId',
          headerName: 'Projekt',
          type: 'string',
          editable: true,
          sortable: false,
          renderCell: (data) => {
            if (isPinnedRow(data)) return '';
            const valueIndex = getValueIndex(values, data.id);
            const findedItem = autocompleteOptions?.project.find(
              (u) => u.value === values.docLine?.[valueIndex]?.projectId
            );
            return findedItem?.label;
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const findedItem = autocompleteOptions?.project.find(
              (u) => u.value === values.docLine?.[valueIndex]?.projectId
            );
            return (
              <Field
                name={`docLine.${valueIndex}.projectId`}
                as={AutoCompleteSelectUI}
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.projectId} />}
                selectedValue={values.docLine?.[valueIndex]?.projectId}
                value={findedItem}
                onChange={(_e, newVal) => {
                  setFieldValue(`docLine.${valueIndex}.projectId`, newVal?.value ?? null);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: newVal?.value ?? null,
                  });

                  const project = autocompleteData.project.find((p) => p.projectId === newVal?.value);
                  setFieldValue(`docLine.${valueIndex}.projectOrderNumber`, project?.orderNumber ?? '');
                }}
                dataset={autocompleteOptions?.project}
                onBlur={async () => await validateField(`docLine.${valueIndex}.projectId`)}
              />
            );
          },
        },

        {
          field: 'projectOrderNumber',
          headerName: getProjectOrderNumberName(params),
          type: 'string',
          editable: false,
          sortable: false,
        },

        {
          field: 'costCenterCode',
          headerName: 'Költséghely',
          type: 'string',
          editable: true,
          sortable: false,
          renderCell: (data) => {
            if (isPinnedRow(data)) return '';
            const valueIndex = getValueIndex(values, data.id);
            const findedItem = autocompleteOptions?.costCenter.find(
              (u) => u.value === values.docLine?.[valueIndex]?.costCenterCode
            );
            return findedItem?.label;
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const findedItem = autocompleteOptions?.costCenter.find(
              (u) => u.value === values.docLine?.[valueIndex]?.costCenterCode
            );
            return (
              <Field
                name={`docLine.${valueIndex}.costCenterCode`}
                as={AutoCompleteSelectUI}
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.costCenterCode} />}
                selectedValue={values.docLine?.[valueIndex]?.costCenterCode}
                value={findedItem}
                onChange={(_e, newVal) => {
                  setFieldValue(`docLine.${valueIndex}.costCenterCode`, newVal?.value ?? null);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: newVal?.value ?? null,
                  });
                }}
                dataset={autocompleteOptions?.costCenter}
                onBlur={async () => await validateField(`docLine.${valueIndex}.costCenterCode`)}
              />
            );
          },
        },

        {
          field: 'itemReference',
          headerName: 'Tétel hivatkozás',
          type: 'string',
          editable: true,
          sortable: false,
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            return (
              <Field
                name={`docLine.${valueIndex}.itemReference`}
                as={InputUI}
                type="text"
                variant="standard"
                fullWidth
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.itemReference} />}
                onChange={(e) => {
                  setFieldValue(`docLine.${valueIndex}.itemReference`, e.target.value ?? '');
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: e.target.value,
                  });
                }}
                onBlur={async () => await validateField(`docLine.${valueIndex}.itemReference`)}
              />
            );
          },
        },

        {
          field: 'internalNote',
          headerName: 'Belső megjegyzés',
          type: 'string',
          editable: true,
          sortable: false,
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            return (
              <Field
                name={`docLine.${valueIndex}.internalNote`}
                as={InputUI}
                type="text"
                variant="standard"
                fullWidth
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.internalNote} />}
                onChange={(e) => {
                  setFieldValue(`docLine.${valueIndex}.internalNote`, e.target.value ?? '');
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: e.target.value,
                  });
                }}
                onBlur={async () => await validateField(`docLine.${valueIndex}.internalNote`)}
              />
            );
          },
        },
        {
          field: 'bpNote',
          headerName: 'Üp megjegyzés',
          type: 'string',
          editable: true,
          sortable: false,
          renderCell: (params) => {
            if (isPinnedRow(params)) return '';
            const value = params?.row?.bpNote ?? '';
            return (
              <Tooltip title={value}>
                <Box
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {value}
                </Box>
              </Tooltip>
            );
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            return (
              <Field
                name={`docLine.${valueIndex}.bpNote`}
                as={InputUI}
                type="text"
                variant="standard"
                fullWidth
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.bpNote} />}
                onChange={(e) => {
                  setFieldValue(`docLine.${valueIndex}.bpNote`, e.target.value ?? '');
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: e.target.value,
                  });
                }}
                onBlur={async () => await validateField(`docLine.${valueIndex}.bpNote`)}
              />
            );
          },
        },
        {
          field: 'lineStatus',
          headerName: 'Státusz',
          type: 'string',
          editable: false,
          sortable: false,
          renderCell: (data) => {
            if (isPinnedRow(data)) return '';
            const valueIndex = getValueIndex(values, data.id);
            return DocStatusList.find((i) => i.value === values.docLine?.[valueIndex]?.lineStatus)?.label;
          },
        },
        {
          field: 'baseDocType',
          headerName: 'Alapbizonylat neve',
          type: 'string',
          editable: false,
          sortable: false,
          renderCell: (data) => {
            if (isPinnedRow(data)) return '';
            const valueIndex = getValueIndex(values, data.id);
            return docTypes.find((i) => i.documentTypeId === values.docLine?.[valueIndex]?.baseDocType)?.name;
          },
        },
        {
          field: 'baseDocnum',
          headerName: 'Alapbizonylat száma',
          type: 'string',
          editable: false,
          sortable: false,
        },
        {
          field: 'baseLine',
          headerName: 'Alapbizonylat sora',
          type: 'string',
          editable: false,
          sortable: false,
        },
        {
          field: 'dueDate',
          headerName: 'Határidő',
          type: 'date',
          sortable: false,
          filterable: false,
          editable: true,
          valueGetter: (_data, row) => {
            return new Date(row.dueDate);
          },
          renderCell: (data) => {
            if (isPinnedRow(data)) return '';
            return dayjs(data.row.dueDate).format('YYYY.MM.DD');
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            return (
              <Field
                name={`docLine.${valueIndex}.dueDate`}
                component={DatePickerUI}
                type="date"
                variant="standard"
                fullWidth
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.dueDate} />}
                value={values.docLine?.[valueIndex]?.dueDate}
                onChange={(date) => {
                  setFieldValue(`docLine.${valueIndex}.dueDate`, date);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: new Date(date),
                  });
                }}
                onBlur={async () => await validateField(`docLine.${valueIndex}.dueDate`)}
              />
            );
          },
        },
        {
          field: 'isBillable',
          headerName: 'Számlázandó',
          type: 'boolean',
          editable: true,
          sortable: false,
          renderCell: (data) => {
            if (isPinnedRow(data)) return '';
            const Icon = data.row?.isBillable ? GridCheckIcon : GridCloseIcon;
            return <Icon fontSize="small" />;
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            return (
              <Field
                name={`docLine.${valueIndex}.isBillable`}
                type="checkbox"
                component={CheckboxUI}
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.isBillable} />}
                checked={values.docLine?.[valueIndex]?.isBillable ?? false}
                variant="standard"
                onChange={(e) => {
                  setFieldValue(`docLine.${valueIndex}.isBillable`, e.target.checked);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: e.target.checked,
                  });
                }}
              />
            );
          },
        },
        {
          field: 'warranty',
          headerName: 'Garanciális',
          type: 'boolean',
          editable: true,
          sortable: false,
          renderCell: (data) => {
            if (isPinnedRow(data)) return '';
            const Icon = data.row?.warranty ? GridCheckIcon : GridCloseIcon;
            return <Icon fontSize="small" />;
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            return (
              <Field
                name={`docLine.${valueIndex}.warranty`}
                type="checkbox"
                component={CheckboxUI}
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.warranty} />}
                checked={values.docLine?.[valueIndex]?.warranty ?? false}
                variant="standard"
                onChange={(e) => {
                  setFieldValue(`docLine.${valueIndex}.warranty`, e.target.checked);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: e.target.checked,
                  });
                }}
              />
            );
          },
        },
        ...(parseInt(docTypeId) === DocumentType.CustomerOrder
          ? [
              {
                field: 'productionPrepared',
                headerName: 'Gyártásra előkészített',
                type: 'boolean',
                editable: true,
                sortable: false,
                renderCell: (data) => {
                  if (isPinnedRow(data)) return '';
                  const Icon = data.row?.productionPrepared ? GridCheckIcon : GridCloseIcon;
                  return <Icon fontSize="small" />;
                },
                renderEditCell: (data) => {
                  const valueIndex = getValueIndex(values, data.id);
                  return (
                    <Field
                      name={`docLine.${valueIndex}.productionPrepared`}
                      type="checkbox"
                      component={CheckboxUI}
                      helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.productionPrepared} />}
                      checked={values.docLine?.[valueIndex]?.productionPrepared ?? false}
                      variant="standard"
                      onChange={(e) => {
                        setFieldValue(`docLine.${valueIndex}.productionPrepared`, e.target.checked);
                        data.api.setEditCellValue({
                          id: data.id,
                          field: data.field,
                          value: e.target.checked,
                        });
                      }}
                    />
                  );
                },
              },
            ]
          : []),
        ...(!currentDocType.inventoryAccounting
          ? [
              {
                field: 'prevPrices',
                headerName: '',
                type: 'string',
                sortable: false,
                filterable: false,
                editable: false,
                minWidth: 56,
                width: 56,
                renderCell: (params) => {
                  if (isPinnedRow(params)) return '';

                  const valueIndex = getValueIndex(values, params.id);
                  const line = values.docLine?.[valueIndex] ?? params.row;
                  const isDisabled = !line?.itemCode;

                  return (
                    <Tooltip title={isDisabled ? 'Előbb adj meg cikkszámot' : 'Korábbi árak'}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={isDisabled}
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();

                            if (!line?.itemCode) return;

                            setPrevPricesDialogState({
                              open: true,
                              line: {
                                itemCode: line.itemCode,
                                itemName: line.itemName,
                              },
                            });
                          }}
                        >
                          <AttachMoneyIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  );
                },
              },
            ]
          : []),
        {
          field: 'actions',
          type: 'actions',
          headerName: '',
          cellClassName: 'actions',
          sortable: false,
          getActions: (params) => {
            if (isPinnedRow(params)) return [];
            const { id, row } = params;
            const foundedItemType = autocompleteData?.item?.find((item) => item.itemCode === row.itemCode)?.type;
            return [
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label="Delete"
                onClick={() => {
                  const valueIndex = getValueIndex(values, id);
                  return remove(valueIndex);
                }}
                color="inherit"
                disabled={
                  disabled ||
                  row.deletable === false ||
                  (currentDocType.inventoryAccounting &&
                    foundedItemType !== ItemGroupType.SERVICE &&
                    !row.isNew &&
                    selectedValues.docStatus !== DocStatus.PLANNED)
                }
              />,
            ];
          },
        },
        ...jokerColumns,
      ]),
    [
      values,
      errors,
      autocompleteData,
      autocompleteOptions,
      currentDocType,
      selectedValues,
      docTypes,
      config,
      documentData,
    ]
  );

  const calculatePrice = (valueIndex, _qty, _unitPrice, _vatPercent) => {
    const qty = parseLocaleNumber(_qty ?? '0');
    const unitPrice = parseLocaleNumber(_unitPrice ?? '0');
    const vatPercent = parseLocaleNumber(_vatPercent ?? '0');

    if (!Number.isFinite(qty) || qty === 0) {
      setFieldValue(`docLine.${valueIndex}.netPrice`, 0);
      setFieldValue(`docLine.${valueIndex}.vatValue`, 0);
      setFieldValue(`docLine.${valueIndex}.grossPrice`, 0);
      setFieldValue(`docLine.${valueIndex}.grossUnitPrice`, 0);

      apiRef.current.setEditCellValue({
        id: values.docLine[valueIndex].id,
        field: 'netPrice',
        value: 0,
      });
      apiRef.current.setEditCellValue({
        id: values.docLine[valueIndex].id,
        field: 'grossPrice',
        value: 0,
      });
      apiRef.current.setEditCellValue({
        id: values.docLine[valueIndex].id,
        field: 'grossUnitPrice',
        value: 0,
      });
      apiRef.current.setEditCellValue({
        id: values.docLine[valueIndex].id,
        field: 'vatValue',
        value: 0,
      });

      return;
    }

    const netPrice = round(multiply(unitPrice, qty), selectedDocCur?.amountRounding ?? 0);
    const vatValue =
      vatPercent > 0 ? round(multiply(netPrice, divide(vatPercent, 100)), selectedDocCur?.amountRounding ?? 0) : 0;

    const grossPrice = round(add(netPrice, vatValue), selectedDocCur?.amountRounding ?? 0);

    const grossUnitPrice = round(divide(grossPrice, qty), docRounding);

    setFieldValue(`docLine.${valueIndex}.netPrice`, netPrice);

    apiRef.current.setEditCellValue({
      id: values.docLine[valueIndex].id,
      field: 'netPrice',
      value: netPrice,
    });

    setFieldValue(`docLine.${valueIndex}.vatValue`, vatValue);
    setFieldValue(`docLine.${valueIndex}.grossPrice`, grossPrice);
    setFieldValue(`docLine.${valueIndex}.grossUnitPrice`, grossUnitPrice);

    const exchangeRate = Number(values.exchangeRate);
    const netPriceInOwnCur = round(
      multiply(netPrice, exchangeRate > 0 ? exchangeRate : 1),
      selectedOwnCur?.amountRounding ?? 0
    );
    const vatValueInOwnCur = round(
      multiply(vatValue, exchangeRate > 0 ? exchangeRate : 1),
      selectedOwnCur?.amountRounding ?? 0
    );
    const grossPriceInOwnCur = round(
      multiply(grossPrice, exchangeRate > 0 ? exchangeRate : 1),
      selectedOwnCur?.amountRounding ?? 0
    );
    setFieldValue(`docLine.${valueIndex}.netPriceInOwnCur`, netPriceInOwnCur);
    setFieldValue(`docLine.${valueIndex}.vatValueInOwnCur`, vatValueInOwnCur);
    setFieldValue(`docLine.${valueIndex}.grossPriceInOwnCur`, grossPriceInOwnCur);
  };

  const calculateWeight = (valueIndex, _qty, _weightChangeNumber) => {
    const qty = parseLocaleNumber(_qty ?? '0');
    const weightChangeNumber = parseLocaleNumber(_weightChangeNumber ?? '0');

    if (!Number.isFinite(qty) || !Number.isFinite(weightChangeNumber)) {
      setFieldValue(`docLine.${valueIndex}.weight`, 0);

      apiRef.current.setEditCellValue({
        id: values.docLine[valueIndex].id,
        field: 'weight',
        value: 0,
      });
      return;
    }

    const weight = round(multiply(qty, weightChangeNumber), 2);
    setFieldValue(`docLine.${valueIndex}.weight`, weight);

    apiRef.current.setEditCellValue({
      id: values.docLine[valueIndex].id,
      field: 'weight',
      value: weight,
    });
  };

  const focusEditorInput = (id, field, { select = true } = {}) => {
    apiRef.current.setCellFocus(id, field);

    requestAnimationFrame(() => {
      const cellEl = apiRef.current.getCellElement?.(id, field);
      if (!cellEl) return;

      const editorInput = cellEl.querySelector('input, textarea, [contenteditable="true"], [tabindex="0"], button');

      if (editorInput) {
        editorInput.focus();
        if (select && typeof editorInput.select === 'function') {
          try {
            editorInput.select();
          } catch {}
        }
      }
    });
  };

  const handleArrowKeyNavigation = async (params, event, push) => {
    const key = event.key;
    const isArrowKey = key === 'ArrowDown' || key === 'ArrowUp';

    if (!isArrowKey || !event.ctrlKey) return;

    event.preventDefault();

    const rowId = params.id;
    if (apiRef.current.getRowMode(rowId) !== GridRowModes.Edit) return;

    apiRef.current.stopRowEditMode({ id: rowId });

    const rowIndex = getValueIndex(values, rowId);
    const offset = key === 'ArrowDown' ? 1 : -1;
    let nextIndex = rowIndex + offset;

    while (
      nextIndex >= 0 &&
      nextIndex < values.docLine.length &&
      values.docLine[nextIndex].lineStatus !== DocStatus.OPENED
    ) {
      nextIndex += offset;
    }

    const nextRow = values.docLine[nextIndex];

    if (key === 'ArrowDown' && !nextRow) {
      await handleClick(push);
      return;
    }

    if (!nextRow) return;

    if (apiRef.current.getRowMode(nextRow.id) === GridRowModes.View) {
      apiRef.current.startRowEditMode({ id: nextRow.id });
    }

    focusedFieldRef.current = params.field;
    focusEditorInput(nextRow.id, params.field);
  };

  const handleTabKeyNavigation = (params, event) => {
    const isTabOrEnter = event.key === 'Tab' || event.key === 'Enter';
    if (!isTabOrEnter) return;

    const rowId = params.id;

    const editableFields = getLineEditableFields(apiRef, rowId, { currentDocType, selectedValues });

    const currentField = params.field;
    const currentIdx = editableFields.indexOf(currentField);
    const dir = event.shiftKey ? -1 : 1;
    const nextIdx = currentIdx + dir;

    if (nextIdx < 0 || nextIdx >= editableFields.length) return;

    event.preventDefault();
    event.defaultMuiPrevented = true;

    const nextField = editableFields[nextIdx];

    focusedFieldRef.current = nextField;
    focusEditorInput(rowId, nextField);
  };

  const [updateLine, setUpdateLine] = useState(null);

  useEffect(() => {
    if (!updateLine) return;

    handleUpdatePastedLine(updateLine);
    setUpdateLine(null);
  }, [updateLine]);

  const handleUpdatePastedLine = async (data) => {
    const { itemCode, qty, unitPrice, newIndex } = data;
    await handleItemChange(newIndex, itemCode);
    handleQtyChange(newIndex, qty);
    handlePriceChange(newIndex, unitPrice ?? '0');
  };

  const [pendingUpdates, setPendingUpdates] = useState([]);

  useEffect(() => {
    if (pendingUpdates.length === 0) return;

    const process = async () => {
      const [next, ...rest] = pendingUpdates;
      const { itemCode, qty, unitPrice, newIndex } = next;

      await handleItemChange(newIndex, itemCode, true);
      handleQtyChange(newIndex, qty);
      handlePriceChange(newIndex, unitPrice ?? '0');

      setPendingUpdates(rest);
    };

    process();
  }, [pendingUpdates]);

  const handleClipboardPaste = async (params, insert) => {
    const { data } = params;

    // kiinduló index a meglévő sorok száma alapján
    const startIndex = values.docLine.length;
    // csak a sikeresen felvett sorok növelik ezt
    let successOffset = 0;

    for (const row of data) {
      const [itemCode, qty, whsCode, unitPrice, internalNote] = row;

      const item = autocompleteData.item.find((i) => i.itemCode === itemCode);
      if (!item) {
        addNotification({
          content: `A(z) ${itemCode} cikk nem létezik !`,
          type: NotificationType.ERROR,
        });
        continue;
      }

      if (isNaN(parseFloat(qty))) {
        addNotification({
          content: `A mennyiség megadása kötelező a(z) ${itemCode} cikkhez!`,
          type: NotificationType.ERROR,
        });
        continue;
      }

      const newLine = {
        ...createNewLineData(),
        ...(whsCode && { whsCode }),
        ...(internalNote && { internalNote }),
      };

      const newIndex = startIndex + successOffset;
      successOffset += 1;
      insert(newIndex, newLine);
      setPendingUpdates((prev) => [...prev, { itemCode, qty, unitPrice, newIndex }]);
    }
  };

  return (
    <div className="overflow-x-auto">
      <div className="align-middle">
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
          <FieldArray name="docLine">
            {({ remove, push, insert }) => (
              <>
                <DatagridPremiumUi
                  key={datagridIdentifiers.docLine}
                  apiRef={apiRef}
                  datagridIdentifier={datagridIdentifiers.docLine}
                  aggregationModel={{}}
                  sx={documentLineGridSx}
                  height={gridHeight}
                  columns={columns(remove)}
                  rows={values?.docLine ?? []}
                  getRowClassName={(params) => (isPinnedRow(params) ? 'docline-pinned-row' : '')}
                  editMode="row"
                  slots={{
                    toolbar: EditToolbar,
                  }}
                  slotProps={{
                    toolbar: {
                      disabled,
                      push,
                      defaultField,
                      apiRef,
                      documentData,
                      selectedCompany,
                      autocompleteData,
                      handleClick,
                    },
                  }}
                  onCellDoubleClick={(params, event) => {
                    event.defaultMuiPrevented = true;
                    if (viewField) return;
                    if (params.field === 'bpNote') {
                      event.defaultMuiPrevented = true;
                      const valueIndex = getValueIndex(values, params.row.id);
                      setNoteDialogState({ open: true, valueIndex });
                      return;
                    }
                  }}
                  onCellClick={(params, event) => {
                    if (viewField) return;
                    if (params.row.lineStatus !== DocStatus.OPENED) return;
                    const rowMode = apiRef.current.getRowMode(params.row.id);
                    if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: params.row.id });
                  }}
                  headerFilters={false}
                  getRowHeight={() => 'auto'}
                  onRowEditStop={() => {
                    focusedFieldRef.current = null;
                  }}
                  onCellKeyDown={(params, event) => {
                    handleArrowKeyNavigation(params, event, push);
                    handleTabKeyNavigation(params, event, push);
                  }}
                  isCellEditable={(params) => (isPinnedRow(params) ? false : baseIsCellEditable(params))}
                  onClipboardPasteStart={(params) => handleClipboardPaste(params, insert)}
                  pinnedRows={{ bottom: [pinnedLine] }}
                />
              </>
            )}
          </FieldArray>
        </div>
      </div>
      {/* ItemDialog külön, nem a FieldArray render-függvényében */}
      <ItemDialog showDialog={itemDialogOpen} onClose={selectItemFromDialog} />
      <ItemStockDialog
        open={itemStockDialogState.open}
        data={itemStockDialogState.data}
        onClose={handleItemStockDialogClose}
      />
      <DocumentPrevPricesDialog
        open={prevPricesDialogState.open}
        onClose={handlePrevPricesDialogClose}
        itemCode={prevPricesDialogState.line?.itemCode}
        itemName={prevPricesDialogState.line?.itemName}
        documentTypeId={currentDocType?.documentTypeId ?? parseInt(docTypeId)}
        currentDirection={currentDocType?.direction}
        docTypes={docTypes}
        initialPartnerId={values.companyId}
        initialPartnerName={values.companyName}
      />
    </div>
  );
};

export default DocumentLine;
